import { Box, Typography } from "@mui/material";
import { Moment } from "moment";
import { Task } from "../api/Tasks";
import { isToday } from "./dates";
import { colorOrNeutral, projectColorText } from "./projectColors";
import { chartRange, dayOffset, placeTasks, Span } from "./ganttSpans";

const dayWidth = 26;
const rowHeight = 34;
const titleWidth = 220;
const headerHeight = 34;

// Gantt lays a set of tasks out over the days they run across, with a connector drawn
// for every dependency between two bars on the chart. Each bar wears the colour of
// the project its task is filed under, so a chart covering more than one project
// still says what each bar belongs to.
export function Gantt(props: {
    tasks: Task[];
    // what to say when none of the tasks can be laid out
    emptyText: string;

    // what to do with a task no date reaches: "list" names it under the chart, "place"
    // draws it where the chain says it goes
    unplaced: "list" | "place";
    selectedId?: number;

    // fontSize is the size of the task titles, in px
    fontSize?: number;
    onSelect: (task: Task) => void;
}) {
    const { spans, unplaced } = placeTasks(props.tasks, props.unplaced === "place");

    if (spans.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {props.emptyText}
            </Typography>
        );
    }

    const range = chartRange(spans);
    const days: Moment[] = [];
    for (let at = range.start.clone(); !at.isAfter(range.end); at.add(1, "day")) {
        days.push(at.clone());
    }

    const width = days.length * dayWidth;
    const rowOf = new Map(spans.map((span, at) => [span.task.id, at]));

    const xOf = (date: Moment) => dayOffset(range.start, date) * dayWidth;

    return (
        <>
            <Box sx={{
                display: "flex",
                overflowX: "auto",
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
            }}>
                <Box sx={{
                    width: titleWidth,
                    flexShrink: 0,
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    borderRight: 1,
                    borderColor: "divider",
                }}>
                    <Box sx={{ height: headerHeight, borderBottom: 1, borderColor: "divider" }} />
                    {spans.map(span => (
                        <Box
                            key={span.task.id}
                            onClick={() => props.onSelect(span.task)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                height: rowHeight,
                                px: 1,
                                cursor: "pointer",
                                borderBottom: 1,
                                borderColor: "divider",
                                bgcolor: span.task.id === props.selectedId ? "action.selected" : undefined,
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <Typography variant="caption" noWrap sx={{
                                fontSize: props.fontSize,
                                textDecoration: span.task.completedAt ? "line-through" : undefined,
                                color: span.task.completedAt ? "text.secondary" : undefined,
                            }}>
                                {span.task.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ position: "relative", width: width, flexShrink: 0 }}>
                    <GanttHeader days={days} />

                    {/* the day lines sit behind the bars rather than inside each row */}
                    {days.map((day, at) => (
                        <Box key={at} sx={{
                            position: "absolute",
                            top: headerHeight,
                            left: at * dayWidth,
                            width: dayWidth,
                            height: spans.length * rowHeight,
                            borderRight: 1,
                            borderColor: "divider",
                            bgcolor: isToday(day) ? "action.hover"
                                : day.day() === 0 || day.day() === 6 ? "grey.50" : undefined,
                        }} />
                    ))}

                    {spans.map((span, at) => {
                        const left = xOf(span.start);
                        const done = !!span.task.completedAt;
                        const color = colorOrNeutral(span.task.projectColor);

                        return (
                            <Box
                                key={span.task.id}
                                onClick={() => props.onSelect(span.task)}
                                title={span.task.title}
                                sx={{
                                    position: "absolute",
                                    top: headerHeight + at * rowHeight + 6,
                                    left: left + 2,
                                    width: Math.max(xOf(span.end) - left + dayWidth - 4, dayWidth - 4),
                                    height: rowHeight - 12,
                                    zIndex: 1,
                                    borderRadius: 0.75,
                                    border: 1,
                                    borderColor: done ? "success.main" : color.border,
                                    bgcolor: done ? "success.light" : color.bg,
                                    color: projectColorText(),
                                    cursor: "pointer",
                                    outline: span.task.id === props.selectedId ? "2px solid" : undefined,
                                    outlineColor: color.border,
                                    "&:hover": { bgcolor: done ? "success.light" : color.hover },
                                }}
                            />
                        );
                    })}

                    <GanttLinks spans={spans} rowOf={rowOf} xOf={xOf} width={width} />
                </Box>
            </Box>

            {unplaced.length > 0 && (
                <Box sx={{ pt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Not scheduled
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ pb: 0.5 }}>
                        No due date, and nothing to follow.
                    </Typography>
                    {unplaced.map(task => (
                        <Typography
                            key={task.id}
                            variant="body2"
                            onClick={() => props.onSelect(task)}
                            sx={{ fontSize: props.fontSize, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                        >
                            {task.title}
                        </Typography>
                    ))}
                </Box>
            )}
        </>
    );
}

function GanttHeader(props: { days: Moment[] }) {
    return (
        <Box sx={{
            display: "flex",
            height: headerHeight,
            borderBottom: 1,
            borderColor: "divider",
            position: "relative",
            zIndex: 1,
        }}>
            {props.days.map((day, at) => (
                <Box key={at} sx={{
                    width: dayWidth,
                    flexShrink: 0,
                    textAlign: "center",
                    borderRight: 1,
                    borderColor: "divider",
                    bgcolor: isToday(day) ? "primary.main" : undefined,
                    color: isToday(day) ? "common.white" : "text.secondary",
                }}>
                    {/* the month only labels the day it starts on, so the strip stays readable */}
                    <Typography variant="caption" component="div" sx={{ fontSize: 9, lineHeight: 1.2 }}>
                        {day.date() === 1 || at === 0 ? day.format("MMM") : " "}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ fontSize: 10, lineHeight: 1.2 }}>
                        {day.date()}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// GanttLinks draws the dependency edges. Each one runs from the end of the task
// being waited on to the start of the task waiting, with the arrowhead at the
// waiting end. An edge is only drawn when both tasks are on the chart.
function GanttLinks(props: {
    spans: Span[];
    rowOf: Map<number, number>;
    xOf: (date: Moment) => number;
    width: number;
}) {
    const paths: string[] = [];

    props.spans.forEach(span => {
        const to = props.rowOf.get(span.task.id);
        if (to === undefined) return;

        span.task.blockedBy.forEach(link => {
            // a blocker with no row is one this chart doesn't lay out, so the edge has
            // nowhere to land. Whether it carries a due date of its own doesn't
            // matter: it may have been placed by its own dependencies.
            const from = props.rowOf.get(link.id);
            if (from === undefined) return;

            const fromSpan = props.spans[from];
            const x1 = props.xOf(fromSpan.end) + dayWidth - 2;
            const y1 = from * rowHeight + rowHeight / 2;
            const x2 = props.xOf(span.start) + 2;
            const y2 = to * rowHeight + rowHeight / 2;

            // step out of the blocker, drop to the dependent's row, then come back in
            const mid = x2 > x1 ? (x1 + x2) / 2 : x1 + 8;
            paths.push(`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`);
        });
    });

    if (paths.length === 0) return null;

    return (
        <Box component="svg"
             width={props.width}
             height={props.spans.length * rowHeight}
             sx={{ position: "absolute", top: headerHeight, left: 0, zIndex: 2, pointerEvents: "none" }}>
            <defs>
                <marker id="gantt-arrow" markerWidth="6" markerHeight="6"
                        refX="5" refY="3" orient="auto">
                    <path d="M 0 0 L 6 3 L 0 6 z" fill="#9e9e9e" />
                </marker>
            </defs>
            {paths.map((path, at) => (
                <path key={at} d={path} fill="none" stroke="#9e9e9e" strokeWidth={1}
                      markerEnd="url(#gantt-arrow)" />
            ))}
        </Box>
    );
}
